import { MimeTypeEnum } from '@/graphql/types';
import Konva from 'konva';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BackgroundIcon,
  DeleteIcon,
  ExportIcon,
  LayersIcon,
  LogoIcon,
  RedoIcon,
  TextIcon,
  UndoIcon,
} from '../../icons/EditorIcons';
import MediaPickerModalButton from '../../inputs/MediaPicker';
import useCanvasHistory from '../hooks/useCanvasHistory';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationType, CanvasImage } from '../types';
import { exportCanvasGif, exportCanvasImage } from '../utils/canvas';

interface MediaPickerFile {
  readonly mediaItemUrl?: string | null;
  readonly title?: string | null;
  readonly id?: string;
  readonly author?: {
    readonly node?: {
      readonly id: string;
      readonly name?: string | null;
      readonly slug?: string | null;
      readonly username?: string | null;
    };
  } | null;
  [key: string]: unknown;
}

interface CanvasSidebarProps {
  hideCreateThumbnail?: boolean;
  hasAnimations: boolean;
}

interface LayerItem {
  id: string;
  type: 'text' | 'image' | 'logo';
  name: string;
  zIndex: number;
}

const CanvasSidebar: React.FC<CanvasSidebarProps> = ({ hideCreateThumbnail = false, hasAnimations }) => {
  const [showBackgroundDropdown, setShowBackgroundDropdown] = useState(false);
  const [showImageDropdown, setShowImageDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showLayersDropdown, setShowLayersDropdown] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);

  const bgDropdownRef = useRef<HTMLDivElement>(null);
  const imageDropdownRef = useRef<HTMLDivElement>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const layersDropdownRef = useRef<HTMLDivElement>(null);

  const selectedId = useCanvasStore((s) => s.selectedId);
  const fileInputRef = useCanvasStore((s) => s.fileInputRef);
  const logoInputRef = useCanvasStore((s) => s.logoInputRef);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const texts = useCanvasStore((s) => s.texts);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const logoImage = useCanvasStore((s) => s.logoImage);
  const logoProps = useCanvasStore((s) => s.logoProps);
  const setBgImage = useCanvasStore((s) => s.setBgImage);
  const setLogoImage = useCanvasStore((s) => s.setLogoImage);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);
  const setIsEditingTextId = useCanvasStore((s) => s.setIsEditingTextId);
  const previewMode = useCanvasStore((s) => s.previewMode);
  const setPreviewMode = useCanvasStore((s) => s.setPreviewMode);
  const resetCanvas = useCanvasStore((s) => s.resetCanvas);

  const normalizeZIndices = useCanvasStore((s) => s.normalizeZIndices);

  const { history, future, saveHistory, undo, redo } = useCanvasHistory();

  const canUndo = history.length > 0;
  const canRedo = future.length > 0;
  const canDelete = !!selectedId && selectedId !== 'canvas';

  const allLayers = useMemo(() => {
    const layers = [];

    texts.forEach((text) => {
      layers.push({
        id: text.id,
        type: 'text',
        name: text.text.length > 20 ? `${text.text.substring(0, 20)}...` : text.text,
        zIndex: text.zIndex || 1,
      });
    });

    canvasImages.forEach((img) => {
      layers.push({
        id: img.id,
        type: 'image',
        name: `Image ${img.id.split('-')[1] || ''}`,
        zIndex: (img as CanvasImage & { zIndex?: number }).zIndex || 1,
      });
    });

    if (logoImage) {
      layers.push({
        id: 'logo',
        type: 'logo',
        name: 'Logo',
        zIndex: logoProps.zIndex || 1,
      });
    }

    return layers.sort((a, b) => b.zIndex - a.zIndex);
  }, [texts, canvasImages, logoImage, logoProps]);

  const handleAddText = () => {
    saveHistory();

    const state = useCanvasStore.getState();
    const textZIndices = state.texts.map((t) => t.zIndex || 1);
    const imageZIndices = state.canvasImages.map((img) => img.zIndex || 1);
    const logoZIndex = state.logoProps.zIndex || 1;

    const allZIndices = [...textZIndices, ...imageZIndices];
    if (state.logoImage) {
      allZIndices.push(logoZIndex);
    }

    const highestZIndex = Math.max(...allZIndices, 2) + 1;

    const newId = `text-${Date.now()}`;
    const newTextItem = {
      id: newId,
      text: 'New Text',
      x: 100,
      y: 100,
      width: 200,
      color: '#000000',
      fontFamily: 'Arial',
      fontSize: 24,
      lineHeight: 1.5,
      letterSpacing: 0,
      align: 'left' as const,
      animation: 'none' as AnimationType,
      zIndex: highestZIndex,
      animationDuration: 0,
      animationDelay: 0,
    };

    setTexts([...texts, newTextItem]);
    setSelectedId(newId);
  };

  const handleDelete = () => {
    if (!canDelete) return;

    saveHistory();

    if (selectedId === 'logo') {
      setLogoImage(null);
      setSelectedId(null);
    } else if (selectedId === 'canvas') {
      return;
    } else {
      const selectedText = texts.find((t) => t.id === selectedId);
      if (selectedText) {
        setIsEditingTextId(null);
        const idToDelete = selectedId;
        setSelectedId(null);
        setTimeout(() => {
          setTexts((prev) => prev.filter((t) => t.id !== idToDelete));
        }, 0);
      } else {
        const selectedImage = canvasImages.find((img) => img.id === selectedId);
        if (selectedImage) {
          const idToDelete = selectedId as string;
          setSelectedId(null);
          setTimeout(() => {
            useCanvasStore.getState().removeImage(idToDelete);
          }, 0);
        }
      }
    }
  };

  const handleExport = (format: 'jpeg' | 'png' | 'gif') => {
    const stage = Konva.stages?.[0];
    if (!stage) return;

    const currentState = useCanvasStore.getState();
    const previousSelectedId = currentState.selectedId;
    const previousEditingTextId = currentState.isEditingTextId;
    const previousPreviewMode = currentState.previewMode;

    try {
      switch (format) {
        case 'jpeg':
        case 'png':
          exportCanvasImage(stage, format);
          break;
        case 'gif':
          if (hasAnimations) {
            useCanvasStore.setState({
              selectedId: null,
              isEditingTextId: null,
              previewMode: false,
            });
            exportCanvasGif(stage, true);
          } else {
            exportCanvasImage(stage, 'png');
            alert('No animations detected. Exported as PNG instead.');
          }
          break;
        default:
          exportCanvasImage(stage, 'png');
      }
    } finally {
      setTimeout(() => {
        useCanvasStore.setState({
          selectedId: previousSelectedId,
          isEditingTextId: previousEditingTextId,
          previewMode: previousPreviewMode,
        });
        setShowExportDropdown(false);
      }, 100);
    }
  };

  const handleMediaSelect = ((file: MediaPickerFile | null) => {
    if (file?.mediaItemUrl) {
      saveHistory();
      setBgImage(file.mediaItemUrl);
      setSelectedId('canvas');
    }
    setShowBackgroundDropdown(false);
  }) as (file: MediaPickerFile | null) => void;

  const triggerBgUpload = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.click();
    }
  };

  const triggerImagesUpload = () => {
    if (logoInputRef?.current) {
      logoInputRef.current.click();
    }
  };

  const handleImageUpload = ((files: MediaPickerFile | MediaPickerFile[] | null) => {
    if (!files) return;

    const fileArray = Array.isArray(files) ? files : [files];

    saveHistory();

    fileArray.forEach((file) => {
      if (file?.mediaItemUrl && useCanvasStore.getState().addImage) {
        useCanvasStore.getState().addImage(file.mediaItemUrl);
      }
    });

    setShowImageDropdown(false);
  }) as (files: MediaPickerFile | MediaPickerFile[] | null) => void;

  const moveLayerDirection = useCallback(
    (id: string, direction: 'up' | 'down') => {
      saveHistory();

      const currentIndex = allLayers.findIndex((layer) => layer.id === id);

      if (
        (direction === 'up' && currentIndex <= 0) ||
        (direction === 'down' && (currentIndex >= allLayers.length - 1 || currentIndex < 0))
      ) {
        return;
      }

      const currentLayer = allLayers[currentIndex];
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      const adjacentLayer = allLayers[targetIndex];

      const tempZIndex = currentLayer.zIndex;
      const newCurrentZIndex = adjacentLayer.zIndex;
      const newAdjacentZIndex = tempZIndex;

      if (currentLayer.type === 'text') {
        useCanvasStore.setState((state) => ({
          texts: state.texts.map((text) =>
            text.id === currentLayer.id ? { ...text, zIndex: newCurrentZIndex } : text
          ),
        }));
      } else if (currentLayer.type === 'image') {
        useCanvasStore.setState((state) => ({
          canvasImages: state.canvasImages.map((img) =>
            img.id === currentLayer.id ? { ...img, zIndex: newCurrentZIndex } : img
          ),
        }));
      } else if (currentLayer.type === 'logo') {
        useCanvasStore.setState((state) => ({
          logoProps: { ...state.logoProps, zIndex: newCurrentZIndex },
        }));
      }

      if (adjacentLayer.type === 'text') {
        useCanvasStore.setState((state) => ({
          texts: state.texts.map((text) =>
            text.id === adjacentLayer.id ? { ...text, zIndex: newAdjacentZIndex } : text
          ),
        }));
      } else if (adjacentLayer.type === 'image') {
        useCanvasStore.setState((state) => ({
          canvasImages: state.canvasImages.map((img) =>
            img.id === adjacentLayer.id ? { ...img, zIndex: newAdjacentZIndex } : img
          ),
        }));
      } else if (adjacentLayer.type === 'logo') {
        useCanvasStore.setState((state) => ({
          logoProps: { ...state.logoProps, zIndex: newAdjacentZIndex },
        }));
      }

      setTimeout(() => {
        normalizeZIndices();
      }, 50);
    },
    [allLayers, saveHistory, normalizeZIndices]
  );

  // Replace the separate functions with calls to the unified function
  const moveLayerUp = useCallback((id: string) => moveLayerDirection(id, 'up'), [moveLayerDirection]);

  const moveLayerDown = useCallback((id: string) => moveLayerDirection(id, 'down'), [moveLayerDirection]);

  const toggleLayersDropdown = () => setShowLayersDropdown((prev) => !prev);
  const toggleExportDropdown = () => setShowExportDropdown((prev) => !prev);

  const handleResetCanvas = () => {
    if (showConfirmReset) {
      resetCanvas();
      setSelectedId(null);
      setShowConfirmReset(false);
    } else {
      setShowConfirmReset(true);
      setTimeout(() => setShowConfirmReset(false), 5000);
    }
  };

  useEffect(() => {
    if (!showExportDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown]);

  useEffect(() => {
    if (allLayers.length > 0 && !previewMode) {
      normalizeZIndices();
    }
  }, [allLayers.length]);

  return (
    <aside className="sidebar zngly-sidebar">
      <header className="sidebar-header">
        <h2 className="sidebar-title">Zngly Canvas Editor</h2>
      </header>

      <section className="sidebar-actions">
        <div
          className="background-dropdown-container"
          style={{ position: 'relative', marginBottom: '8px' }}
          ref={bgDropdownRef}
        >
          <button
            type="button"
            onClick={() => setShowBackgroundDropdown((prev) => !prev)}
            className="zngly-button zngly-button-primary zngly-button-full"
            disabled={previewMode}
          >
            <span className="button-icon">
              <BackgroundIcon />
            </span>
            <span>Background</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                transition: 'transform 0.3s ease',
                transform: showBackgroundDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              ▼
            </span>
          </button>

          {showBackgroundDropdown && (
            <div
              className="background-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                borderRadius: '0 0 6px 6px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                marginTop: '2px',
                overflow: 'visible',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  triggerBgUpload();
                  setShowBackgroundDropdown(false);
                }}
                className="zngly-button zngly-button-full"
                style={{ borderRadius: 0, margin: 0, borderBottom: '1px solid #eee' }}
              >
                Upload from Device
              </button>

              <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', zIndex: 1001 }}>
                <MediaPickerModalButton
                  buttonTitle="From Media Library"
                  mimeTypes={[
                    MimeTypeEnum.ImageJpeg,
                    MimeTypeEnum.ImagePng,
                    MimeTypeEnum.ImageGif,
                    MimeTypeEnum.ImageWebp,
                  ]}
                  onFileSelect={handleMediaSelect}
                  showThumbnailButton={false}
                />
              </div>
            </div>
          )}
        </div>

        <div
          className="image-dropdown-container"
          style={{ position: 'relative', marginBottom: '8px' }}
          ref={imageDropdownRef}
        >
          <button
            type="button"
            onClick={() => setShowImageDropdown((prev) => !prev)}
            className="zngly-button zngly-button-secondary zngly-button-full"
            disabled={previewMode}
          >
            <span className="button-icon">
              <LogoIcon />
            </span>
            <span>Add Images</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                transition: 'transform 0.3s ease',
                transform: showImageDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              ▼
            </span>
          </button>

          {showImageDropdown && (
            <div
              className="image-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                borderRadius: '0 0 6px 6px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                marginTop: '2px',
                overflow: 'visible',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  triggerImagesUpload();
                  setShowImageDropdown(false);
                }}
                className="zngly-button zngly-button-full"
                style={{ borderRadius: 0, margin: 0, borderBottom: '1px solid #eee' }}
              >
                Upload from Device
              </button>

              <div
                onClick={(e) => e.stopPropagation()}
                className="logo-dropdown"
                style={{ position: 'relative', zIndex: 1001 }}
              >
                <MediaPickerModalButton
                  buttonTitle="From Media Library"
                  onFileSelect={handleImageUpload}
                  showThumbnailButton={false}
                  mimeTypes={[
                    MimeTypeEnum.ImageJpeg,
                    MimeTypeEnum.ImagePng,
                    MimeTypeEnum.ImageGif,
                    MimeTypeEnum.ImageWebp,
                  ]}
                />
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleAddText}
          className="zngly-button zngly-button-tertiary"
          disabled={previewMode}
        >
          <span className="button-icon">
            <TextIcon />
          </span>
          <span>Add Text</span>
        </button>

        {!hideCreateThumbnail && (
          <button
            type="button"
            onClick={() => {}}
            className="zngly-button zngly-button-tertiary"
            disabled={previewMode}
          >
            <span className="button-icon" />
            <span>Create Thumbnail</span>
          </button>
        )}

        <div
          className="layers-dropdown-container"
          style={{ position: 'relative', marginTop: '8px', marginBottom: '8px' }}
          ref={layersDropdownRef}
        >
          <button
            type="button"
            onClick={toggleLayersDropdown}
            className="zngly-button zngly-button-tertiary zngly-button-full"
            disabled={previewMode}
          >
            <span className="button-icon">
              <LayersIcon />
            </span>
            <span>Layers</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                transition: 'transform 0.3s ease',
                transform: showLayersDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              ▼
            </span>
          </button>

          {showLayersDropdown && (
            <div
              className="layers-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                borderRadius: '0 0 6px 6px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                marginTop: '2px',
                maxHeight: '250px',
                overflowY: 'auto',
              }}
            >
              {allLayers.length === 0 ? (
                <div style={{ padding: '6px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                  No layers to display
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {allLayers.map((layer, index) => (
                    <li
                      key={layer.id}
                      onClick={() => setSelectedId(layer.id)}
                      style={{
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        backgroundColor: layer.id === selectedId ? '#f0f9ff' : 'white',
                        fontWeight: layer.id === selectedId ? 'bold' : 'normal',
                        color: layer.id === selectedId ? 'var(--zngly-primary)' : 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          marginRight: '6px',
                          backgroundColor:
                            layer.type === 'text' ? '#4299e1' : layer.type === 'image' ? '#48bb78' : '#ed8936',
                          borderRadius: '2px',
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {layer.name}
                      </span>
                      <span style={{ fontSize: '9px', color: '#666', marginLeft: '4px' }}>
                        {layer.type} (z:{layer.zIndex})
                      </span>
                      <div className="layer-controls" style={{ marginLeft: '4px', display: 'flex', gap: '2px' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLayerUp(layer.id);
                          }}
                          disabled={index === 0}
                          style={{
                            border: 'none',
                            background: 'none',
                            padding: '2px',
                            cursor: index === 0 ? 'default' : 'pointer',
                            opacity: index === 0 ? 0.3 : 1,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title="Move layer up"
                        >
                          <ArrowUpIcon size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLayerDown(layer.id);
                          }}
                          disabled={index === allLayers.length - 1}
                          style={{
                            border: 'none',
                            background: 'none',
                            padding: '2px',
                            cursor: index === allLayers.length - 1 ? 'default' : 'pointer',
                            opacity: index === allLayers.length - 1 ? 0.3 : 1,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title="Move layer down"
                        >
                          <ArrowDownIcon size={12} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div
                style={{
                  padding: '6px',
                  borderTop: '1px solid #eee',
                  fontSize: '10px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                Use arrows to adjust layer order
              </div>
            </div>
          )}
        </div>
      </section>

      <hr className="divider" />

      <section className="button-group">
        <button
          type="button"
          onClick={undo}
          className="zngly-button zngly-button-icon"
          title="Undo"
          disabled={!canUndo || previewMode}
          aria-label="Undo"
        >
          <UndoIcon />
        </button>

        <button
          type="button"
          onClick={redo}
          className="zngly-button zngly-button-icon"
          title="Redo"
          disabled={!canRedo || previewMode}
          aria-label="Redo"
        >
          <RedoIcon />
        </button>

        <button
          type="button"
          onClick={handleDelete}
          className="zngly-button zngly-button-icon zngly-button-danger"
          title="Delete"
          disabled={!canDelete || previewMode}
          aria-label="Delete selected element"
        >
          <DeleteIcon />
        </button>
      </section>

      <footer className="sidebar-footer">
        <button
          type="button"
          className={`zngly-button ${previewMode ? 'zngly-button-primary active' : 'zngly-button-secondary'} zngly-button-full`}
          onClick={() => setPreviewMode(!previewMode)}
          style={{
            marginBottom: '10px',
            color: previewMode ? '#fff' : undefined,
            border: previewMode ? '2px solid #3498db' : undefined,
            fontWeight: previewMode ? 'bold' : undefined,
          }}
          disabled={!hasAnimations && !previewMode}
          title={hasAnimations ? 'Click to preview animations' : 'No animations to preview'}
        >
          <span>{previewMode ? 'Exit Preview' : 'Animation Preview'}</span>
        </button>

        <div
          className="export-dropdown-container"
          style={{ position: 'relative', marginBottom: '10px' }}
          ref={exportDropdownRef}
        >
          <button
            type="button"
            onClick={toggleExportDropdown}
            className="zngly-button zngly-button-success zngly-button-full"
          >
            <span className="button-icon">
              <ExportIcon />
            </span>
            <span>Export Image</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                transition: 'transform 0.3s ease',
                transform: showExportDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              ▼
            </span>
          </button>

          {showExportDropdown && (
            <div
              className="export-dropdown"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 100,
                backgroundColor: 'white',
                borderRadius: '0 0 6px 6px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                marginTop: '2px',
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => handleExport('png')}
                className="zngly-button zngly-button-full"
                style={{ borderRadius: 0, margin: 0, borderBottom: '1px solid #eee' }}
              >
                PNG
              </button>
              <button
                type="button"
                onClick={() => handleExport('jpeg')}
                className="zngly-button zngly-button-full"
                style={{ borderRadius: 0, margin: 0, borderBottom: '1px solid #eee' }}
              >
                JPEG
              </button>
              <button
                type="button"
                onClick={() => handleExport('gif')}
                className="zngly-button zngly-button-full"
                style={{ borderRadius: 0, margin: 0 }}
                disabled={!hasAnimations}
                title={!hasAnimations ? 'No animations to export' : 'Export as animated GIF (3s)'}
              >
                GIF {hasAnimations ? '(3s)' : ''}
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleResetCanvas}
          className={`zngly-button ${showConfirmReset ? 'zngly-button-danger' : 'zngly-button-tertiary'} zngly-button-full`}
          disabled={previewMode}
        >
          <span className="button-icon">{showConfirmReset ? '!' : '↺'}</span>
          <span>{showConfirmReset ? 'Click to Confirm Reset' : 'Reset Canvas'}</span>
        </button>
      </footer>
    </aside>
  );
};

export default CanvasSidebar;
